/**
 * Kalshi exchange-shard identity carried by market frames.
 *
 * This module deliberately stops at observation. Routing is a separate
 * campaign: a market's observed index is useful context, but it is not write
 * authority and must never be guessed from a ticker, sport, or missing value.
 */

export type KalshiExchangeIndexState =
  | 'missing'
  | 'valid'
  | 'malformed'
  | 'contradictory';

export interface KalshiExchangeIndexDiagnostic {
  state: KalshiExchangeIndexState;
  /** Present only for a single valid observation. Zero is a real value. */
  exchangeIndex?: number;
  /** Retained for diagnostics when a producer emitted an invalid value. */
  malformedValue?: unknown;
  /** Sorted distinct valid values witnessed for one ticker. */
  conflictingIndexes?: number[];
}

const hasOwn = (value: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key);

export function isKalshiExchangeIndex(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    Number.isInteger(value) &&
    value >= 0
  );
}

/** Read one frame without treating missing as shard zero. */
export function readKalshiExchangeIndex(
  frame: object | null | undefined,
): KalshiExchangeIndexDiagnostic {
  if (!frame || !hasOwn(frame, 'exchangeIndex')) return { state: 'missing' };
  const value = (frame as { exchangeIndex?: unknown }).exchangeIndex;
  if (!isKalshiExchangeIndex(value)) {
    return { state: 'malformed', malformedValue: value };
  }
  return { state: 'valid', exchangeIndex: value };
}

/**
 * Fold a new observation into the per-ticker diagnostic. A contradiction is
 * sticky for the process lifetime so a later agreeing/missing frame cannot
 * hide that two authoritative-looking frames disagreed.
 */
export function observeKalshiExchangeIndex(
  previous: KalshiExchangeIndexDiagnostic | undefined,
  frame: object | null | undefined,
): KalshiExchangeIndexDiagnostic {
  const next = readKalshiExchangeIndex(frame);
  if (previous?.state === 'contradictory') return previous;
  if (
    previous?.state === 'valid' &&
    next.state === 'valid' &&
    previous.exchangeIndex !== next.exchangeIndex
  ) {
    return {
      state: 'contradictory',
      conflictingIndexes: [previous.exchangeIndex!, next.exchangeIndex!].sort(
        (a, b) => a - b,
      ),
    };
  }
  return next;
}
